// src/components/InvoicePrint.js
import React, { forwardRef } from "react";
import "../styles/InvoicePrint.css";

/* ========= Helpers ========= */
const LOCALE = "pt-PT";
const CURRENCY = "EUR";

const nfCurrency =
  typeof Intl !== "undefined"
    ? new Intl.NumberFormat(LOCALE, {
        style: "currency",
        currency: CURRENCY,
        minimumFractionDigits: 2,
      })
    : null;

const nfDate =
  typeof Intl !== "undefined"
    ? new Intl.DateTimeFormat(LOCALE, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

const safe = (v, fallback = "—") =>
  v === 0 ? "0" : v == null || v === "" ? fallback : v;

const currency = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return nfCurrency ? nfCurrency.format(n) : `${n.toFixed(2)} €`;
};

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return nfDate ? nfDate.format(date) : date.toLocaleDateString(LOCALE);
};

/* ========= VAT ========= */
/*
  IVA temporariamente neutro.

  Quando criares o campo de IVA na ficha do cliente,
  a fatura deve receber esse valor em invoice.vatRate.
*/

/**
 * Props:
 * - invoice: {
 *     invoiceId, date, paymentDueDate,
 *     pickup, delivery,
 *     trailerPlate, truckPlate,
 *     items, price,
 *     vatRate, taxRate, discount, status
 *   }
 * - issuer: {
 *     name, address, postalCode, city, country,
 *     phone, email, logoUrl,
 *     paymentMethod, iban, bic, paymentQrUrl
 *   }
 * - client: {
 *     name, address, postalCode, city, country,
 *     phone, email
 *   }
 * Backward-compat: if only 'company' is passed, it's treated as issuer.
 */
const InvoicePrint = forwardRef(
  ({ invoice = {}, issuer, client, company }, ref) => {
    // backward-compat
    const issuerIn = issuer || company || {};

    const {
      invoiceId,
      date,
      paymentDueDate,
      pickup,
      delivery,
      trailerPlate,
      truckPlate,
      items,
      price,
      discount,
    } = invoice;

    // Lines (fallback: single line with total price)
    const lines =
      Array.isArray(items) && items.length
        ? items.map((l) => ({
            description: l.description,
            qty: Number(l.qty ?? 1) || 1,
            unit: Number(l.price ?? l.unit ?? l.total ?? 0) || 0,
            total:
              Number(
                l.total ??
                  (Number(l.qty ?? 1) || 1) * (Number(l.price ?? 0) || 0)
              ) || 0,
          }))
        : [
            {
              description: `Transporte ${safe(pickup)} → ${safe(delivery)}`,
              qty: 1,
              unit: Number(price || 0),
              total: Number(price || 0),
            },
          ];

    const base = lines.reduce((s, l) => s + (Number(l.total) || 0), 0);

    const vatRateRaw =
      invoice.vatRate ??
      invoice.taxRate ??
      invoice.iva ??
      null;

    const vatRate =
      vatRateRaw === null || vatRateRaw === undefined || vatRateRaw === ""
        ? null
        : Number(String(vatRateRaw).replace(",", "."));

    const hasVatRate = Number.isFinite(vatRate);

    const disc = Number(discount || 0);
    const taxableBase = Math.max(0, base - disc);
    const vat = hasVatRate
      ? Number(((taxableBase * vatRate) / 100).toFixed(2))
      : null;

    const total = taxableBase + (vat || 0);

    const dateOut = fmtDate(date);
    const dueOut = fmtDate(paymentDueDate);

    // ===== Issuer & Client blocks =====
    const fmtEmail = (v) => (v ? String(v).trim() : "");
    const fmtPhone = (v) => (v ? String(v).trim() : "");

    // Issuer (logged company)
    const iName = safe(issuerIn.name, "Nome da Empresa");
    const iAddr = safe(issuerIn.address, "");
    const iCityLine = [
      safe(issuerIn.postalCode, ""),
      safe(issuerIn.city, ""),
      safe(issuerIn.country, ""),
    ]
      .filter(Boolean)
      .join(", ");

      const iVat = safe(
        issuerIn.vat ||
          issuerIn.vat_tax_id ||
          issuerIn.vatTaxId ||
          issuerIn.vatTaxID ||
          issuerIn.VAT_TaxInformation ||
          issuerIn.vatTaxInformation ||
          issuerIn.company_registration_number ||
          issuerIn.companyRegistrationNumber ||
          issuerIn.nif ||
          issuerIn.taxId,
        ""
      );
      
      console.log("[InvoicePrint] issuerIn =", issuerIn);
      console.log("[InvoicePrint] resolved issuer NIF =", iVat);
      
      const iPhoneEmail = [fmtPhone(issuerIn.phone), fmtEmail(issuerIn.email)]
        .filter(Boolean)
        .join(" | ");

    // Client (bill to)
    const cName = safe(client?.name, "Nome da Empresa");

    const cVat = safe(
      client?.VAT_TaxInformation ||
        client?.vatTaxInformation ||
        client?.vat_tax_id ||
        client?.vatTaxId ||
        client?.vatTaxID ||
        client?.vat ||
        client?.vatNumber ||
        client?.nif ||
        client?.taxId,
      ""
    );
    
    console.log("[InvoicePrint] client =", client);
    console.log("[InvoicePrint] resolved client NIF =", cVat);
    
    const cAddr = safe(client?.address, "");
    const cCityLine = [
      safe(client?.postalCode, ""),
      safe(client?.city, ""),
      safe(client?.country, ""),
    ]
      .filter(Boolean)
      .join(", ");

    // Telefone primeiro, depois email
    const cPhoneEmail = [fmtPhone(client?.phone), fmtEmail(client?.email)]
      .filter(Boolean)
      .join(" | ");

    const vehicleOut = safe(truckPlate || trailerPlate, "—");

    return (
      <div className="sheetA4 neoBg" ref={ref}>
        {/* ===== HEADER ===== */}
        <header className="headerSimple">
          {/* LOGO */}
          <div className="logoRow">
            {issuerIn.logoUrl ? (
              <img
                src={issuerIn.logoUrl}
                alt="logótipo"
                className="headLogo"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="headLogo--ph">LOGO</div>
            )}
          </div>

          {/* 2 columns: left (issuer + invoice) | right (client) */}
          <div className="twoCol">
            {/* Left */}
            <div className="leftCol">
              {/* Issuer */}
              <div className="companyBlock companyBlock--issuer">
                <div className="companyName">{iName}</div>
                {iVat && <div className="muted">NIF: {iVat}</div>}
                {iAddr && <div className="muted">{iAddr}</div>}
                {iCityLine && <div className="muted">{iCityLine}</div>}
                {iPhoneEmail && <div className="muted">{iPhoneEmail}</div>}
              </div>

              {/* Invoice meta */}
              <div className="invMetaPlain invMetaPlain--stack">
                <div className="invMetaTitlePlain">Documento Provisório de Cobrança</div>

                <div className="invMetaRow">
                  <span className="invLabel">N.º do Documento:</span>
                  <span className="invValue">{safe(invoiceId)}</span>
                </div>

                <div className="invMetaRow">
                  <span className="invLabel">Data do Documento:</span>
                  <span className="invValue">
                    {dateOut === "—" ? "DD/MM/AAAA" : dateOut}
                  </span>
                </div>

                <div className="invMetaRow">
                  <span className="invLabel">Data de Vencimento:</span>
                  <span className="invValue">
                    {dueOut === "—" ? "DD/MM/AAAA" : dueOut}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: CLIENT */}
            <div className="rightCol">
              <div className="billToCard">
                <div className="billToLabel">Cliente</div>

                <div className="companyBlock companyBlock--client">
                  <div className="companyName">{cName}</div>
                  {cVat && <div className="muted">NIF: {cVat}</div>}
                  {cAddr && <div className="muted">{cAddr}</div>}
                  {cCityLine && <div className="muted">{cCityLine}</div>}
                  {cPhoneEmail && <div className="muted">{cPhoneEmail}</div>}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* ===== TABLE ===== */}
        <table className="gridTable gridTable--minimal">
          <thead>
            <tr>
              <th className="col-date">DATA</th>
              <th className="col-loc">ORIGEM</th>
              <th className="col-loc">DESTINO</th>
              <th className="col-price right">PREÇO (EUR)</th>
            </tr>
          </thead>

          <tbody>
            <tr className="row-main">
              <td className="muted">
                {dateOut === "—" ? "DD/MM/AAAA" : dateOut}
              </td>
              <td>{safe(pickup)}</td>
              <td>{safe(delivery)}</td>
              <td className="right strong">{currency(base)}</td>
            </tr>

            {Array.isArray(items) &&
              items.length > 0 &&
              items.map((l, idx) => (
                <tr className="row-sub" key={idx}>
                  <td className="muted"></td>
                  <td colSpan={2} className="descCell muted">
                    {safe(l.description, "—")}
                  </td>
                  <td className="right muted">
                    {Number(l.qty ?? 1) || 1} ×{" "}
                    {currency(Number(l.price ?? l.unit ?? 0))}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div className="flexSpacer" />

        {/* ===== TOTALS ===== */}
        <div className="totalsSlim">
          <div className="totalsSlim__head">
            <div>BASE</div>
            <div>IVA</div>
            <div>DESCONTO</div>
            <div>TOTAL</div>
          </div>

          <div className="totalsSlim__values">
            <div>{currency(base || 0)}</div>
            <div>
            {hasVatRate
              ? `${currency(vat || 0)} (${vatRate.toFixed(2).replace(".", ",")}%)`
              : "—"}
            </div>
            <div>{currency(disc || 0)}</div>
            <div className="totalsSlim__total">{currency(total || 0)}</div>
          </div>
        </div>

        {/* ===== PAYMENT ===== */}
        <div className="paySlim paySlim--mock">
          <div className="paySlim__left">
            <strong>MÉTODO DE PAGAMENTO:</strong>&nbsp;
            <span>{safe(issuerIn?.paymentMethod, "transferência")}</span>
          </div>

          <div className="paySlim__right">
            <div>
              <strong>IBAN:</strong> {safe(issuerIn.iban, "—")}
            </div>
            <div>
              <strong>BIC:</strong> {safe(issuerIn.bic, "—")}
            </div>
          </div>
        </div>

        <div className="footNote">
          Obrigado por trabalhar com <strong>{iName}</strong>. Este documento foi
          gerado através do LOADEvo. Não constitui uma fatura fiscal.
        </div>
      </div>
    );
  }
);

export default InvoicePrint;